// PYK Ekranı (ön ödemeli kart: bakiye yükleme / iade / oturum) kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'sales.pykScreen';

export const salesPykScreenGuide = (t: TFunction): GuideContent => ({
  layout: 'stacked',
  title: t(`${G}.title`, 'PYK Ekranı nasıl kullanılır?'),
  intro: t(`${G}.intro`, 'PYK ekranında müşterinin kartını okutur, karta bakiye yükler, kalan bakiyeyi iade eder ve kartın oturumunu açıp kapatırsınız. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Başlık ve kasiyer'),
    form: t(`${G}.regions.form`, 'Kart okuma, kart bilgisi ve bakiyeler'),
    list: t(`${G}.regions.list`, 'Son işlemler'),
    footer: t(`${G}.regions.footer`, 'İşlem düğmeleri'),
  },
  steps: [
    {
      region: 'form',
      title: t(`${G}.s1.title`, '1. Kartı okutun'),
      text: t(`${G}.s1.text`, 'Kartı okuyucuya okutun ya da kart UID / numarasını "Kart Okuma" kutusuna yazıp Enter\'a basın veya "Okut"a tıklayın. Kart bulunursa "KART BİLGİLERİ" ve "OTURUM DURUMU" görünür. Başka bir karta geçmek için "Kapat"a basın.'),
      tips: [
        t(`${G}.s1.tip1`, 'Kart bulunamazsa kutunun altında "Kart bulunamadı" yazar.'),
        t(`${G}.s1.tip2`, 'Ekranda kasiyer sistemi açıksa önce PIN ile kasiyer girişi istenir; işlemler kasiyer adına kaydedilir. Sağ üstteki çıkış simgesi kasiyeri değiştirir.'),
        t(`${G}.s1.tip3`, 'Kasiyerin yetkisi yoksa bakiye yükleme, iade veya oturum işleminde "yetkiniz yok" uyarısı çıkar.'),
      ],
    },
    {
      region: 'footer',
      title: t(`${G}.s2.title`, '2. Oturumu başlatın, bakiyeyi görün'),
      text: t(`${G}.s2.text`, 'Kartta "OTURUM YOK" yazıyorsa "Oturum Başlat"a basın. Oturum açılınca "CÜZDAN BAKİYELERİ" ve TOPLAM bakiye görünür. Bakiye yükleme ve iade yalnız aktif oturumda yapılır.'),
      tips: [
        t(`${G}.s2.tip1`, 'Karta bağlı bir cari varsa oturum o cari adına açılır.'),
        t(`${G}.s2.tip2`, '"Oturum Başlat" / "Oturum Kapat" düğmeleri görünmüyorsa ekran ayarlarında gizlenmiştir.'),
      ],
    },
    {
      region: 'footer',
      title: t(`${G}.s3.title`, '3. Bakiye yükleyin'),
      text: t(`${G}.s3.text`, '"Bakiye Yükle"ye basın. Cüzdan\'ı seçin (tek cüzdan varsa kendiliğinden seçilir), Tutar\'ı yazın, isterseniz Açıklama ekleyin. Müşterinin ödeme şekline göre "NAKİT" veya "POS" düğmesine basın. Tutar seçilen cüzdanın bakiyesine eklenir.'),
      tips: [
        t(`${G}.s3.tip1`, 'NAKİT: para ekran ayarlarındaki nakit kasasına kaydedilir. Yazarkasa cihazına hiçbir şey gönderilmez, fiş kesilmez.'),
        t(`${G}.s3.tip2`, 'POS: tutar ayarlardaki POS banka hesabına kaydedilir. Yazarkasa PavoPay ise cihazdan avans POS ödemesi alınır; Ingenico ise ayarlarda seçilen ürün, yüklenen tutar kadar satılır. Cihazda ödeme alınamazsa veya iptal edilirse bakiye yüklenmez.'),
        t(`${G}.s3.tip3`, 'NAKİT ve POS düğmeleri görünmüyorsa PYK ekran ayarlarından nakit kasası veya POS banka hesabı tanımlanmalıdır.'),
        t(`${G}.s3.tip4`, 'Ingenico\'da işlem yarım kalırsa ekran açılışında, kart okutulduğunda veya "Yarım İşlem Kontrol" ile "Cihazda Yarım Kalmış İşlem" penceresi çıkar. Ödeme alınmışsa "Fişi Kapat ve Bakiye Yükle" (ya da yalnız "Sadece Fişi Kapat"), alınmamışsa "İşlemi İptal Et" seçilir; iptalde bakiye yüklenmez.'),
      ],
    },
    {
      region: 'footer',
      title: t(`${G}.s4.title`, '4. Bakiyeyi iade edin'),
      text: t(`${G}.s4.text`, '"Bakiye İade"ye basın. Kartta PavoPay ile yapılmış POS yüklemesi yoksa "Bakiye İade" penceresi açılır: cüzdanı seçin, tutar cüzdanın bakiyesiyle dolu gelir. "NAKİT İADE" ile para nakit kasasından ödenir ve karttaki bakiyeden düşülür.'),
      tips: [
        t(`${G}.s4.tip1`, 'Yazarkasa Ingenico ise iade her zaman nakit olarak kasadan yapılır.'),
        t(`${G}.s4.tip2`, 'Nakit kasası tanımlı değilse "NAKİT İADE" düğmesi görünmez; önce ekran ayarlarından kasa tanımlayın.'),
      ],
    },
    {
      region: 'footer',
      title: t(`${G}.s5.title`, '5. POS ile yüklenmiş kartta iade dağılımı'),
      text: t(`${G}.s5.text`, 'Kartta PavoPay ile POS yüklemesi varsa "Bakiye İade - POS Avans Dağılımı" penceresi açılır. Harcamalar önce POS yüklemelerinden düşülür. Karta nakit yüklenen para asla POS\'a gönderilmez: harcanmamış nakit kısım kasadan nakit iade edilir, POS yüklemelerinin kalan kısmı Pavo cihazından POS iadesi olarak işlenir.'),
      tips: [
        t(`${G}.s5.tip1`, 'Örnek: 100 TL POS + 50 TL nakit yüklendi, 80 TL harcandı → 20 TL POS iadesi + 50 TL nakit iade.'),
        t(`${G}.s5.tip2`, '"Dağıtım Hesabı" tablosu yüklenen, harcanan ve iade edilecek tutarları gösterir. "İade Tutarı" kutusunda satır tutarını değiştirebilirsiniz; yükleme tutarını aşamaz.'),
        t(`${G}.s5.tip3`, '"POS İade" tutarı müşterinin kartına (ilk ödemeye bağlı olarak) iade eder; "Nakit İade" aynı tutarı Pavo cihazında nakit iade olarak işler. İki durumda da nakit kısım ayrıca kasadan iade edilir.'),
        t(`${G}.s5.tip4`, 'Cihazda iade iptal edilirse veya hata olursa işlem durur, sonraki iadeler yapılmaz.'),
      ],
    },
    {
      region: 'list',
      title: t(`${G}.s6.title`, '6. Son işlemleri ve geçmişi inceleyin'),
      text: t(`${G}.s6.text`, '"SON İŞLEMLER" tablosu aktif oturumdaki Yükleme, İade ve Satın Alma hareketlerini; ödeme tipini (Nakit / POS / Kart), tutarı ve işlem sonrası bakiyeyi gösterir. Satın almalarda "Fiş Görüntüle" simgesi alınan ürünleri açar; "Tüm Satın Alınan Ürünler" oturumdaki tüm ürünleri listeler.'),
      tips: [
        t(`${G}.s6.tip1`, '"Geçmiş" kartın önceki oturumlarını (müşteri, durum, başlangıç, bitiş) gösterir.'),
      ],
    },
    {
      region: 'footer',
      title: t(`${G}.s7.title`, '7. Oturumu kapatın'),
      text: t(`${G}.s7.text`, '"Oturum Kapat" kartın oturumunu bitirir. Cüzdanda bakiye kalmışsa oturum kapatılmaz; önce bakiyeyi iade edin. Sağ üstteki (i) bu rehberi yeniden açar.'),
    },
  ],
});
